import { FileTextOutlined, FilterFilled } from "@ant-design/icons";
import { message, notification, Pagination, Table, Tooltip } from "antd";
import type {
  TablePaginationConfig,
  SorterResult,
} from "antd/es/table/interface";
import moment from "moment";
import React, { useCallback, useEffect, useState } from "react";

import CommentIcon from "../../../../svgs/comment.svg?react";
import {
  Asset,
  DepreciableCategory,
  IMessageType,
  ITransaction,
  KontaxTransactionFilterOptions,
  KontaxTransactionMeta,
  TransactionSource,
  UpdateTransactionState,
  ValueCategorizationType,
  AccountOption,
} from "../../../../types";
import {
  destroyMessage,
  formatAmountInCents,
  showGraphQlErrorNotification,
  showLoadingMessage,
  showMessage,
} from "../../../../utils";
import {
  onClickStopPropagation,
  shouldCellUpdate,
} from "../../../../utils/table";
import { useDeleteBusinessAssetConfirmation } from "../../../BusinessAsset/useDeleteBusinessAssetConfirmation";
import { getToBeDeletedBusinessAssets } from "../../../BusinessAsset/utils";
import { useKontaxUserContext } from "../../../contexts/KontaxUserContext";
import Copyable from "../../../common/Copyable";
import { HoverableCommentIcon } from "../../../common/styledComponents";
import { TransactionSplitState } from "../../../common/types";
import { CategorizeBatchPopup } from "./CategorizeBatchPopup";
import { DEFAULT_PAGE_SIZE } from "./constants";
import DownloadTransactionsModal from "./DownloadTransactionsModal";
import {
  ExternalTransactionInitialValues,
  ExternalTransactionPopup,
} from "./ExternalTransactionPopup";
import {
  CheckboxCell,
  InvoiceIcon,
  MessageWrapper,
  PaginationWrapper,
  TableWrapper,
} from "./styledComponents";
import { TransactionsTableButtons } from "./TransactionsTableButtons";
import CsvImportModal from "./CsvImport/CsvImportModal";
import {
  deriveInvoiceFilteredValue,
  deriveNoteFilteredValue,
  deriveSubmittedFilteredValue,
  deriveVerifiedFilteredValue,
} from "./filterValues";
import { TransactionsTableCategory } from "./TransactionsTableCategory";
import { TransactionsTableExpandedRow } from "./TransactionsTableExpandRow";
import { TransactionsTablePopups } from "./TransactionsTablePopups";
import { TransactionsVatCategory } from "./TransactionsTableVatCategory";
import TransactionSubmissionStatus, {
  TransactionSubmissionState,
} from "./TransactionSubmissionStatus";
import { TransactionRow, UpdateSplitsState } from "./types";
import {
  findBusinessAssetsToUpdateAmount,
  getDatevSource,
  getTransactionRowClassName,
  prepareTransactionsData,
} from "./utils";
import VerificationField from "./VerificationField";
import { DISPLAY_DATE_FORMAT } from "@/v1/constants";
import { useBulkDeleteExternalTransactionsMutation } from "@/v1/api/graphql/mutations/transaction/bulkDeleteExternalTransactions";
import { isDepreciationRow } from "./depreciationRows";

const DELETABLE_SOURCES: TransactionSource[] = [
  TransactionSource.USER,
  TransactionSource.BACKOFFICE_MANUAL,
  TransactionSource.CSV_IMPORT,
];

interface TransactionsProps {
  email: string | undefined;
  updateTransactionsMeta: (
    payload: Array<KontaxTransactionMeta>
  ) => Promise<boolean>;
  transactions: Array<ITransaction> | null;
  transactionsCount?: number;
  // Consumed by the download-export modal (SMT-1225) so the export always
  // requests the exact same filters as the table currently displays. Required
  // (not optional) on purpose: a caller that forgets to pass this would
  // otherwise silently export every transaction the customer has, unfiltered.
  requestFilters: KontaxTransactionFilterOptions;
  pagination?: {
    page: number;
    pageSize: number;
  };
  onPaginationChange?: (page: number, pageSize: number) => void;
  onSortChange?: (field: string, direction: "asc" | "desc" | null) => void;
  onFilterChange?: (field: string, value: any) => void;
  onSplitSubmit: (transactionId: string, state: TransactionSplitState) => void;
  onSplitDelete: (transactionId: string, state: TransactionSplitState) => void;
  onAssetsUpdate: (transactionId: string, assets: Array<Asset>) => void;
  updateTransactionsState: (
    state:
      | { transactions: Array<ITransaction> | null; count?: number }
      | ((prev: {
          transactions: Array<ITransaction> | null;
          count?: number;
        }) => { transactions: Array<ITransaction> | null; count?: number })
  ) => void;
  selectedTransactions: Array<ITransaction>;
  hasSelectedVerifiedTransactionsOnly?: boolean;
  verifySelectedTransactions: () => void;
  setSelection: (selectedTransactionIds: string[]) => void;
  onCsvImportSuccess?: () => void;
  onTransactionRefresh?: (transactionId: string) => Promise<void>;
  isNested?: boolean;
  currency?: string;
  currencyOptions?: string[];
  accountsById?: Map<string, AccountOption>;
  // Current values of the other column filters, held in the parent. AntD table
  // filtering is all-or-nothing: to keep filters combinable, every filter
  // column must be controlled (SMT-1163), so each column derives its
  // `filteredValue` from these.
  assetsFilter?: boolean;
  noteFilters?: {
    internalNote: boolean | null;
    personalNote: boolean | null;
  };
  verifiedFilter?: boolean;
  verifiedByMLFilter?: boolean;
  submissionStatusFilter?: string | null;
}

const copyToClipboard = (text: string) => {
  navigator.clipboard.writeText(text);
  message.success("IBAN copied");
};

const TransactionsTable = ({
  email,
  updateTransactionsMeta,
  transactions,
  transactionsCount,
  requestFilters,
  pagination,
  onPaginationChange,
  onSortChange,
  onFilterChange,
  onSplitSubmit,
  onSplitDelete,
  onAssetsUpdate,
  updateTransactionsState,
  selectedTransactions,
  hasSelectedVerifiedTransactionsOnly,
  verifySelectedTransactions,
  setSelection,
  onCsvImportSuccess,
  onTransactionRefresh,
  isNested,
  currency,
  currencyOptions = [],
  accountsById,
  assetsFilter,
  noteFilters = { internalNote: null, personalNote: null },
  verifiedFilter,
  verifiedByMLFilter,
  submissionStatusFilter = null,
}: TransactionsProps) => {
  const [updatingSplits, setUpdatingSplits] =
    useState<UpdateSplitsState | null>(null);

  const [showExternalTransactionPopup, setShowExternalTransactionPopup] =
    useState(false);

  const [showCsvImportModal, setShowCsvImportModal] = useState(false);

  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const { user } = useKontaxUserContext();
  /*
   * KontaxUserContext keeps the PREVIOUS customer's `user` truthy while it
   * refetches after a customer switch, so `!!user` alone would leave the button
   * live during that window -- and the export is addressed by `user.id`, so a
   * click landing in it would hand back the previous customer's transactions
   * while the table already shows the new one. Nothing downstream can catch
   * that: the endpoint trusts its own path parameter by design.
   *
   * Require the loaded user to actually be the customer the table is filtered
   * to. That is false during the refetch and after a failed one, which is
   * exactly when the button should be dead.
   */
  const canDownload = !!user && !!email && user.email === email;

  const [isCategorizeBatchPopupVisible, setIsCategorizeBatchPopupVisible] =
    useState(false);

  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);

  const [activeExternalTransaction, setActiveExternalTransaction] =
    useState<ITransaction>();

  const firstTransactionId = transactions?.length && transactions[0].id;
  useEffect(() => {
    setExpandedRowKeys([]);
  }, [firstTransactionId]);

  const [deleteBusinessAssetConfirmationPopup, confirmDeleteBusinessAsset] =
    useDeleteBusinessAssetConfirmation();

  const handleTablePaginationChange = useCallback(
    (
      _paginationConfig: TablePaginationConfig,
      filters: any,
      sorter: SorterResult<TransactionRow> | SorterResult<TransactionRow>[]
    ) => {
      // Handle sorting for columns
      if (onSortChange) {
        const sorterResult = Array.isArray(sorter) ? sorter[0] : sorter;
        if (
          sorterResult &&
          sorterResult.field &&
          typeof sorterResult.field === "string"
        ) {
          const direction =
            sorterResult.order === "ascend"
              ? "asc"
              : sorterResult.order === "descend"
              ? "desc"
              : null;

          // Map column field names to API field names
          const fieldMapping: Record<string, string> = {
            paymentDate: "valuta_date",
            iban: "iban",
            amount: "amount",
            description: "description",
            paidBy: "name",
            verified: "verified",
            vatCategoryCode: "vat_category_code",
            categoryCode: "category_code",
          };

          const apiFieldName =
            fieldMapping[sorterResult.field] || sorterResult.field;
          onSortChange(apiFieldName, direction);
        }
      }

      // Handle filtering for columns
      if (onFilterChange && filters) {
        // Handle assets filter (invoice column)
        if (filters.invoice !== undefined && filters.invoice !== null) {
          const filterValue = filters.invoice;
          if (Array.isArray(filterValue) && filterValue.length > 0) {
            // Filter is active: value 1 = has assets (true), value 0 = no assets (false)
            const hasAssets = filterValue[0] === 1;
            onFilterChange("assets", hasAssets);
          } else {
            // Filter is cleared
            onFilterChange("assets", undefined);
          }
        } else if (filters.invoice === null) {
          // Filter is explicitly cleared
          onFilterChange("assets", undefined);
        }

        // Handle note filter
        if (filters.note !== undefined && filters.note !== null) {
          const filterValue = filters.note;
          if (Array.isArray(filterValue) && filterValue.length > 0) {
            const selectedValues = filterValue as number[];
            // value 0 = Internal Note, value 1 = User's Note, value 2 = All
            if (selectedValues.includes(2)) {
              // "All" is selected: both true
              onFilterChange("internalNote", true);
              onFilterChange("personalNote", true);
            } else {
              // Individual filters
              const hasInternal = selectedValues.includes(0);
              const hasPersonal = selectedValues.includes(1);

              onFilterChange("internalNote", hasInternal ? true : null);
              onFilterChange("personalNote", hasPersonal ? true : null);
            }
          } else {
            // Filter is cleared
            onFilterChange("internalNote", null);
            onFilterChange("personalNote", null);
          }
        } else if (filters.note === null) {
          // Filter is explicitly cleared
          onFilterChange("internalNote", null);
          onFilterChange("personalNote", null);
        }

        // Handle verified filter
        if (filters.verified !== undefined && filters.verified !== null) {
          const filterValue = filters.verified;
          if (Array.isArray(filterValue) && filterValue.length > 0) {
            const selectedValue = filterValue[0];
            // Handle "ml" (Verified by ML) as a special case
            if (selectedValue === "ml") {
              onFilterChange("verifiedByML", true);
              onFilterChange("verified", undefined);
            } else {
              onFilterChange("verified", selectedValue as boolean);
              onFilterChange("verifiedByML", undefined);
            }
          } else {
            // Filter is cleared
            onFilterChange("verified", undefined);
            onFilterChange("verifiedByML", undefined);
          }
        } else if (filters.verified === null) {
          // Filter is explicitly cleared
          onFilterChange("verified", undefined);
          onFilterChange("verifiedByML", undefined);
        }

        // Handle submission status filter ("Submitted" column)
        if (filters.submitted !== undefined && filters.submitted !== null) {
          const filterValue = filters.submitted;
          if (Array.isArray(filterValue) && filterValue.length > 0) {
            const selectedValue = filterValue[0] as TransactionSubmissionState;
            onFilterChange("submissionStatus", selectedValue);
          } else {
            // Filter is cleared
            onFilterChange("submissionStatus", null);
          }
        } else if (filters.submitted === null) {
          // Filter is explicitly cleared
          onFilterChange("submissionStatus", null);
        }

        // Handle currency filter (lives on the "Amount" column)
        if (filters.amount !== undefined && filters.amount !== null) {
          const filterValue = filters.amount;
          if (Array.isArray(filterValue) && filterValue.length > 0) {
            onFilterChange("currency", filterValue[0] as string);
          } else {
            // Filter is cleared
            onFilterChange("currency", undefined);
          }
        } else if (filters.amount === null) {
          // Filter is explicitly cleared
          onFilterChange("currency", undefined);
        }
      }
    },
    [onSortChange, onFilterChange]
  );

  /**
   * Update transaction meta.
   * If transaction's category has changed, associated business asset will be removed.
   * In that case, a delete confirmation modal will be displayed.
   * Updated data will be saved into the component's state, and will be processed when user clicks the confirm button.
   * Otherwise, data will be cleared from the state.
   */
  const handleUpdateTransaction = useCallback(
    async ({
      businessAssets,
      newCategoryCode,
      kontistTransactionId,
      verified,
    }: UpdateTransactionState) => {
      if (verified) {
        setExpandedRowKeys(
          expandedRowKeys.filter((key) => key !== kontistTransactionId)
        );
      }
      const loadingKey = "updating-transaction";

      try {
        showLoadingMessage(loadingKey);
        const confirmedDeleteBusinessAsset = await confirmDeleteBusinessAsset(
          getToBeDeletedBusinessAssets({
            id: kontistTransactionId,
            businessAssets,
            categoryCode: newCategoryCode,
          })
        );

        if (!confirmedDeleteBusinessAsset) {
          return;
        }

        if (verified !== undefined) {
          await updateTransactionsMeta([
            {
              kontistTransactionId,
              verified,
            },
          ]);
        } else {
          await updateTransactionsMeta([
            {
              kontistTransactionId,
              categoryCode: newCategoryCode,
              categoryCodeMeta: {
                label: undefined,
                suggestionSource: undefined,
                categorizationType: ValueCategorizationType.TAX_OPS,
              },
            },
          ]);
        }
      } catch (error) {
        showGraphQlErrorNotification("Error while updating transaction", error);
      } finally {
        destroyMessage(loadingKey);
      }
    },
    [updateTransactionsMeta, expandedRowKeys, confirmDeleteBusinessAsset]
  );

  /**
   * Update split.
   * If split's category has changed, associated business asset will be removed.
   * In that case, a delete confirmation modal will be displayed.
   * Updated data will be saved into the component's state, and will be processed when user clicks the confirm button.
   * Otherwise, data will be cleared from the state.
   */
  const handleUpdateSplits = useCallback(
    async ({
      transaction,
      splitState,
      skipConfirmationPopup = false,
    }: UpdateSplitsState) => {
      const confirmedDeleteBusinessAsset = await confirmDeleteBusinessAsset(
        getToBeDeletedBusinessAssets({
          id: transaction.id,
          businessAssets: transaction.businessAssets,
          splits: splitState.splits,
        })
      );
      if (!confirmedDeleteBusinessAsset) {
        return;
      }

      if (!skipConfirmationPopup) {
        // Split amount has changed
        const assetsToUpdateAmount = findBusinessAssetsToUpdateAmount({
          oldSplits: transaction.splits,
          newSplits: splitState.splits,
          businessAssets: transaction.businessAssets,
        });

        if (assetsToUpdateAmount?.length) {
          return setUpdatingSplits({
            transaction,
            splitState,
            assetsToUpdateAmount,
          });
        }
      }

      // Default submit behaviour
      // If the modal was shown, hide it after changing category
      setUpdatingSplits(null);

      await onSplitSubmit(transaction.id, splitState);
    },
    [onSplitSubmit, confirmDeleteBusinessAsset]
  );

  const handleDeleteSplit = useCallback(
    async (transaction: TransactionRow, state: TransactionSplitState) => {
      const confirmedDeleteBusinessAsset = await confirmDeleteBusinessAsset(
        getToBeDeletedBusinessAssets({
          id: transaction.id,
          businessAssets: transaction.businessAssets,
        })
      );
      if (!confirmedDeleteBusinessAsset) {
        return false;
      }

      onSplitDelete(transaction.id, state);
      return true;
    },
    [confirmDeleteBusinessAsset, onSplitDelete]
  );

  const onTransactionUpdated = useCallback(
    (upsertedTransaction: ITransaction) => {
      const updatedTransactions = (transactions || [])
        .filter((transaction) => transaction.id !== upsertedTransaction.id)
        .concat([upsertedTransaction])
        .sort((a: ITransaction, b: ITransaction) =>
          moment(b.valutaDate).isAfter(a.valutaDate) ? 1 : -1
        );

      updateTransactionsState((prev) => ({
        ...prev,
        transactions: updatedTransactions,
      }));

      notification.success({
        message: "Created or updated transaction successfully.",
      });
    },
    [transactions, updateTransactionsState]
  );

  const onTransactionDeleted = useCallback(
    (transactionId: string) => {
      const updatedTransactions = (transactions || [])
        .filter((transaction) => transaction.id !== transactionId)
        .sort((a: ITransaction, b: ITransaction) =>
          moment(b.valutaDate).isAfter(a.valutaDate) ? 1 : -1
        );

      updateTransactionsState((prev) => ({
        ...prev,
        transactions: updatedTransactions,
      }));

      notification.success({
        message: "Transaction deleted successfully.",
      });
    },
    [transactions, updateTransactionsState]
  );

  const [bulkDeleteExternalTransactionsMutation] =
    useBulkDeleteExternalTransactionsMutation();

  const deletableSelected = selectedTransactions.filter(
    (t) =>
      DELETABLE_SOURCES.includes(t.source as TransactionSource) && !t.verified
  );

  const bulkDeleteSelected = useCallback(async () => {
    const toDelete = deletableSelected;
    if (!toDelete.length || !email) return;
    try {
      const { data } = await bulkDeleteExternalTransactionsMutation({
        variables: {
          email,
          transactionIds: toDelete.map((t) => t.id),
        },
      });
      const deletedCount = data?.bulkDeleteExternalTransactions ?? 0;
      const deletedIds = new Set(toDelete.map((t) => t.id));
      const updatedTransactions = (transactions || []).filter(
        (t) => !deletedIds.has(t.id)
      );
      updateTransactionsState((prev) => ({
        ...prev,
        transactions: updatedTransactions,
      }));
      setSelection([]);
      notification.success({
        message: `${deletedCount} transaction${
          deletedCount !== 1 ? "s" : ""
        } deleted successfully.`,
      });
    } catch {
      notification.error({ message: "Failed to delete transactions." });
    }
  }, [
    deletableSelected,
    bulkDeleteExternalTransactionsMutation,
    email,
    transactions,
    updateTransactionsState,
    setSelection,
  ]);

  const columns = [
    {
      title: "Amount",
      key: "amount",
      dataIndex: "amount",
      width: 100,
      render: (amount: number, record: TransactionRow) => (
        <div data-testid="amountCell">
          {formatAmountInCents(amount, true, undefined, record.currency)}
        </div>
      ),
      sorter: true,
      align: "right" as const,
      showSorterTooltip: false,
      shouldCellUpdate,
      // Currency filter lives on the Amount column: the amount already shows the
      // currency inline, so we only add the filter funnel here instead of a
      // separate Currency column. The tooltip makes clear the funnel filters by
      // currency (the sort control on the same header still sorts by amount).
      filters: currencyOptions.map((code) => ({ text: code, value: code })),
      filterMultiple: false,
      filteredValue: currency ? [currency] : null,
      filterIcon: (filtered: boolean) => (
        <Tooltip title="Filter by currency">
          <FilterFilled style={{ color: filtered ? "#1890ff" : undefined }} />
        </Tooltip>
      ),
    },
    {
      title: <FileTextOutlined className="centerIcon" />,
      key: "invoice",
      width: 60,
      render: (record: TransactionRow) => {
        return (record.assets || []).length > 0 ? (
          <InvoiceIcon data-testid="fileIcon" />
        ) : null;
      },
      align: "center" as const,
      shouldCellUpdate,
      filters: [
        {
          text: "Has Receipt",
          value: 1,
        },
        {
          text: "No Receipt",
          value: 0,
        },
      ],
      filterMultiple: false,
      filteredValue: deriveInvoiceFilteredValue(assetsFilter),
    },
    {
      title: () => {
        return <CommentIcon />;
      },
      key: "note",
      width: 60,
      render: (record: TransactionRow) => {
        return record.internalNote || record.personalNote ? (
          <HoverableCommentIcon />
        ) : null;
      },
      filters: [
        {
          text: "Internal Note",
          value: 0,
        },
        {
          text: "User's Note",
          value: 1,
        },
        {
          text: "All",
          value: 2,
        },
      ],
      filterMultiple: false,
      filteredValue: deriveNoteFilteredValue(noteFilters),
      align: "center" as const,
      shouldCellUpdate,
    },
    {
      title: "Paid by",
      key: "paidBy",
      dataIndex: "paidBy",
      width: 140,
      render: (paidBy: string) => <div data-testid="paid-by">{paidBy}</div>,
      sorter: true,
      showSorterTooltip: false,
      shouldCellUpdate,
    },
    {
      title: "Bank",
      key: "bank",
      width: 60,
      align: "center" as const,
      render: (_: unknown, record: TransactionRow) => {
        const account = record.externalAccountId
          ? accountsById?.get(record.externalAccountId)
          : undefined;

        // Legacy transactions (no external_account_id) or an account we can't
        // resolve yet render empty ("main account").
        if (!account) {
          return (
            <span
              data-testid="bank-cell-empty"
              style={{ color: "rgba(0, 0, 0, 0.45)" }}
            >
              —
            </span>
          );
        }

        const tooltip =
          [account.accountName, account.institutionName]
            .filter(Boolean)
            .join(" · ") ||
          account.iban ||
          "";

        return (
          <Tooltip placement="topLeft" title={tooltip}>
            {account.institutionLogo ? (
              <img
                data-testid="bank-logo"
                src={account.institutionLogo}
                alt={account.institutionName ?? "Bank"}
                style={{
                  width: 20,
                  height: 20,
                  objectFit: "contain",
                  borderRadius: 4,
                }}
              />
            ) : (
              <span data-testid="bank-cell-nologo">
                {account.institutionName ?? "—"}
              </span>
            )}
          </Tooltip>
        );
      },
      // No shouldCellUpdate here: the cell depends on `accountsById`, which is
      // fetched asynchronously and separately from the rows. A record-only
      // comparison would leave the logo stuck on the "—" placeholder when the
      // account map arrives after the rows have already rendered.
    },
    {
      title: "IBAN",
      key: "iban",
      dataIndex: "iban",
      width: 90,
      render: (iban: string | null, record: TransactionRow) => (
        <Tooltip placement="topLeft" title={iban}>
          <Copyable withFixedIcon ellipsis>
            {iban}
          </Copyable>
        </Tooltip>
      ),
      sorter: true,
      showSorterTooltip: false,
      ellipsis: {
        showTitle: false,
      },
      onCell: (record: TransactionRow) => {
        return {
          onClick: (event: any) => {
            event.stopPropagation();
            if (record.iban) copyToClipboard(record.iban);
          },
        };
      },
      shouldCellUpdate,
    },
    {
      title: "Description",
      key: "description",
      dataIndex: "description",
      width: 150,
      sorter: true,
      showSorterTooltip: false,
      shouldCellUpdate,
    },
    {
      title: "Payment Date",
      key: "paymentDate",
      dataIndex: "paymentDate",
      width: 120,
      sorter: true,
      defaultSortOrder: "descend" as const,
      showSorterTooltip: false,
      align: "center" as const,
      shouldCellUpdate,
      render: (paymentDate: string) => {
        return moment(paymentDate).format(DISPLAY_DATE_FORMAT);
      },
    },
    {
      title: "Verified",
      key: "verified",
      dataIndex: "verified",
      width: 130,
      render: (verified: boolean | undefined, record: TransactionRow) => {
        if (isDepreciationRow(record)) {
          return null;
        }

        return (
          <div data-testid="verificationCell">
            <VerificationField
              categoryCode={record.categoryCode}
              vatCategoryCode={record.vatCategoryCode}
              categoryCodeMeta={record.categoryCodeMeta}
              vatCategoryCodeMeta={record.vatCategoryCodeMeta}
              id={record.id}
              updateTransaction={handleUpdateTransaction}
              value={verified || false}
              isSplitCategorized={record.isSplitCategorized}
              isDatevTransaction={!!getDatevSource(record)}
              verifiedBy={record.verifiedBy}
              hasAssets={!!record.assets?.length}
            />
          </div>
        );
      },
      filters: [
        {
          text: "Verified",
          value: true,
        },
        {
          text: "Not Verified",
          value: false,
        },
        {
          text: "Verified by ML",
          value: "ml",
        },
      ],
      filterMultiple: false,
      filteredValue: deriveVerifiedFilteredValue(
        verifiedFilter,
        verifiedByMLFilter
      ),
      sorter: true,
      showSorterTooltip: false,
      onCell: onClickStopPropagation,
      shouldCellUpdate,
    },
    {
      title: "Category",
      key: "categoryCode",
      dataIndex: "categoryCode",
      width: 348,
      render: (
        _categoryCode: string | null | undefined,
        record: TransactionRow
      ) => {
        if (isDepreciationRow(record)) {
          return null;
        }

        return (
          <TransactionsTableCategory
            record={record}
            handleUpdateTransaction={handleUpdateTransaction}
          />
        );
      },
      sorter: true,
      showSorterTooltip: false,
      onCell: onClickStopPropagation,
      shouldCellUpdate,
    },
    {
      title: "VAT Category",
      key: "vatCategoryCode",
      dataIndex: "vatCategoryCode",
      width: 348,
      render: (
        _vatCategoryCode: string | null | undefined,
        record: TransactionRow
      ) => {
        if (isDepreciationRow(record)) {
          return null;
        }

        return (
          <TransactionsVatCategory
            record={record}
            updateTransactionsMeta={updateTransactionsMeta}
          />
        );
      },
      sorter: true,
      showSorterTooltip: false,
      onCell: onClickStopPropagation,
      shouldCellUpdate,
    },
    {
      title: "Submitted",
      key: "submitted",
      width: 150,
      render: (record: TransactionRow) =>
        isDepreciationRow(record) ? null : (
          <TransactionSubmissionStatus record={record} />
        ),
      filters: [
        {
          text: "Submitted",
          value: TransactionSubmissionState.SUBMITTED,
        },
        {
          text: "Not submitted",
          value: TransactionSubmissionState.NOT_SUBMITTED,
        },
        {
          text: "Fibu changed",
          value: TransactionSubmissionState.FIBU_CHANGED,
        },
        {
          text: "VCC changed",
          value: TransactionSubmissionState.VCC_CHANGED,
        },
        {
          text: "Fibu & VCC changed",
          value: TransactionSubmissionState.MULTIPLE_CHANGES,
        },
      ],
      filterMultiple: false,
      filteredValue: deriveSubmittedFilteredValue(submissionStatusFilter),
      onCell: onClickStopPropagation,
      shouldCellUpdate,
    },
  ];

  const data = prepareTransactionsData(transactions);

  const renderExpandedRow = (transaction: TransactionRow) => {
    if (isDepreciationRow(transaction)) {
      return null;
    }

    return (
      <TransactionsTableExpandedRow
        transaction={transaction}
        onAssetsUpdate={onAssetsUpdate}
        handleDeleteSplits={handleDeleteSplit}
        handleUpdateSplits={handleUpdateSplits}
        updateTransactionsMeta={updateTransactionsMeta}
        editExternalTransactionButtonOnClick={() => {
          setShowExternalTransactionPopup(true);
          setActiveExternalTransaction(transaction);
        }}
        onTransactionUpdated={onTransactionUpdated}
        onTransactionDeleted={onTransactionDeleted}
        onTransactionRefresh={onTransactionRefresh}
      />
    );
  };

  if (!transactions) {
    return (
      <MessageWrapper>
        <p>Search for transactions by email</p>
      </MessageWrapper>
    );
  }

  const paginationControls = !isNested && (
    <PaginationWrapper>
      <Pagination
        showSizeChanger
        pageSizeOptions={["50", "100", "200", "300"]}
        total={transactionsCount}
        /*
         * SMT-1318: the total was already fetched for the page math but never
         * shown, so operators estimated open transactions from the page count.
         * Rendering it through the pager guarantees the label and the pages
         * always describe the same result set. Wording matches the old Belege
         * View's count, which is what Tax Ops is used to reading.
         */
        showTotal={(total) =>
          /*
           * Not the shared getSingularOrPlural helper: it tests only the last
           * digit, so 11 and 21 would render "11 transaction found".
           */
          `${total} ${total === 1 ? "transaction" : "transactions"} found`
        }
        current={pagination?.page ?? 1}
        pageSize={pagination?.pageSize ?? DEFAULT_PAGE_SIZE}
        onChange={(page, pageSize) => {
          onPaginationChange?.(page, pageSize);
        }}
      />
    </PaginationWrapper>
  );

  const onExpand = async (expanded: boolean, record: TransactionRow) => {
    if (isDepreciationRow(record)) {
      return;
    }

    const keys = expanded ? [record.key] : [];
    setExpandedRowKeys(keys);
  };

  const toggleShowCategorizeBatch = () => {
    if (
      selectedTransactions.some(
        (t) =>
          t.categoryCode &&
          Object.values<string>(DepreciableCategory).includes(t.categoryCode)
      )
    ) {
      showMessage({
        type: IMessageType.ERROR,
        content:
          "It is not possible to recategorize transactions with category 1320 or 1370",
      });
      return;
    }

    if (selectedTransactions.some((t) => t.verified)) {
      showMessage({
        type: IMessageType.ERROR,
        content: "It is not possible to recategorize verified transactions",
      });
      return;
    }
    if (selectedTransactions.some((t) => t.splits?.length)) {
      showMessage({
        type: IMessageType.ERROR,
        content: "It is not possible to batch-categorize splits",
      });
      return;
    }
    setIsCategorizeBatchPopupVisible(!isCategorizeBatchPopupVisible);
  };

  return (
    <>
      <ExternalTransactionPopup
        isPopupShown={showExternalTransactionPopup}
        onSuccess={onTransactionUpdated}
        email={email as string}
        closePopup={() => {
          setShowExternalTransactionPopup(false);
          setActiveExternalTransaction(undefined);
        }}
        initialValues={
          activeExternalTransaction as ExternalTransactionInitialValues
        }
      />

      <CategorizeBatchPopup
        isPopupVisible={isCategorizeBatchPopupVisible}
        closePopup={() => {
          toggleShowCategorizeBatch();
        }}
        selectedTransactions={selectedTransactions}
        updateTransactionsMeta={updateTransactionsMeta}
      />

      {email && (
        <CsvImportModal
          email={email}
          open={showCsvImportModal}
          onClose={() => setShowCsvImportModal(false)}
          onSuccess={() => onCsvImportSuccess?.()}
        />
      )}

      {/* Rendered only while actually open, so it mounts fresh (and picks up
          the current customer's profile-language default) every time it's
          opened, instead of staying mounted with stale state across a
          customer switch. */}
      {user && showDownloadModal && (
        <DownloadTransactionsModal
          visible={showDownloadModal}
          onClose={() => setShowDownloadModal(false)}
          userId={user.id}
          userLanguage={user.language}
          user={user}
          filters={requestFilters}
          rowCount={transactionsCount ?? 0}
        />
      )}

      <TransactionsTableButtons
        addExternalTransactionClick={() => {
          setShowExternalTransactionPopup(true);
        }}
        onCsvImportClick={() => setShowCsvImportModal(true)}
        onDownloadClick={() => setShowDownloadModal(true)}
        canDownload={canDownload}
        hasSelectedTransactions={!!selectedTransactions.length}
        hasSelectedVerifiedTransactionsOnly={
          hasSelectedVerifiedTransactionsOnly
        }
        verifySelectedTransactions={verifySelectedTransactions}
        toggleShowCategorizeBatch={toggleShowCategorizeBatch}
        deletableSelectedCount={deletableSelected.length}
        onBulkDeleteConfirm={bulkDeleteSelected}
        isNested={isNested}
      />

      {paginationControls}

      <TableWrapper>
        <Table
          data-testid="verificationTable"
          rowSelection={{
            getCheckboxProps: (record) => ({
              disabled: isDepreciationRow(record),
            }),
            onChange: (selectedTransactionIds: React.Key[]) => {
              setSelection(selectedTransactionIds as string[]);
            },
            selectedRowKeys: selectedTransactions.map((t) => t.id),
            renderCell: (_checked, _record, _i, node) => {
              // We use this logic to be able to select the checkbox
              // also when clicking somewhere else on the cell
              // but still persist ant'shift + click logic
              return (
                <CheckboxCell
                  data-testid={`checkbox-row-${_i}`}
                  onClick={(event) => {
                    event.stopPropagation();

                    const checkbox =
                      event.currentTarget.querySelector<HTMLInputElement>(
                        'input[type="checkbox"]'
                      );

                    checkbox?.dispatchEvent(
                      new globalThis.MouseEvent(
                        event.nativeEvent.type,
                        event.nativeEvent
                      )
                    );
                  }}
                >
                  {node}
                </CheckboxCell>
              );
            },
          }}
          columns={columns}
          dataSource={data}
          bordered
          expandable={{
            expandedRowRender: renderExpandedRow,
            expandedRowClassName: () => "expandable-row",
            onExpand,
            rowExpandable: (record) => !isDepreciationRow(record),
            expandedRowKeys,
            expandIconColumnIndex: -1,
          }}
          pagination={false}
          onChange={handleTablePaginationChange}
          scroll={{ x: "100%" }}
          rowClassName={getTransactionRowClassName}
          expandRowByClick
          size="middle"
          sticky
        />

        {paginationControls}

        <TransactionsTablePopups
          handleUpdateSplits={handleUpdateSplits}
          updatingSplits={updatingSplits}
        />
        {deleteBusinessAssetConfirmationPopup}
      </TableWrapper>
    </>
  );
};

export default TransactionsTable;
